import { Heading, Text } from "@chakra-ui/react";
import { useTranslations } from "contexts/LocalizationContext";
import getUrlFromLink from "utils/getUrlFromLink";
import { AirHelpScoreWrapper } from "../AirHelpScoreWrapper";
import type { AirportRankingTemplateProps } from "../AirHelpScoreWrapper";
import {
  About,
  GlobalRanking,
  Hero,
  HowScoreIsCalculated,
  JoinTheCommunity,
  ScoreTag,
  Share,
  TopThree,
} from "../common";
import TopThreeCard from "../common/TopThree/TopThreeCard";
import AirportRankingTable from "./AirportRankingTable";
import columns from "./AirportRankingTable/config";

export const AirportRanking = ({
  story,
  cmsLanguagesData,
  serverSideStoryblokPages,
  airportsScores,
  googleOptimizeExperimentIds,
  whiteListDomains,
  nofollowUrls,
}: AirportRankingTemplateProps) => {
  const translations = useTranslations();
  const topThreeAirports = airportsScores.slice(0, 3);

  const {
    content: {
      heroHeading,
      heroSubHeading,
      heroLink,
      heroText,
      howIsItCalculatedHeading,
      howIsItCalculatedFirstItemHeading,
      howIsItCalculatedFirstItemIcon,
      howIsItCalculatedFirstItemText,
      howIsItCalculatedSecondItemHeading,
      howIsItCalculatedSecondItemIcon,
      howIsItCalculatedSecondItemText,
      howIsItCalculatedThirdItemHeading,
      howIsItCalculatedThirdItemIcon,
      howIsItCalculatedThirdItemText,
      globalRankingHeading,
      globalRankingImageSrc,
      globalRankingText,
      aboutHeading,
      aboutText,
      aboutVideoSrc,
      aboutImageSrc,
    },
  } = story;

  const howIsItCalculatedItems = [
    {
      heading: howIsItCalculatedFirstItemHeading,
      icon: howIsItCalculatedFirstItemIcon,
      text: howIsItCalculatedFirstItemText,
    },
    {
      heading: howIsItCalculatedSecondItemHeading,
      icon: howIsItCalculatedSecondItemIcon,
      text: howIsItCalculatedSecondItemText,
    },
    {
      heading: howIsItCalculatedThirdItemHeading,
      icon: howIsItCalculatedThirdItemIcon,
      text: howIsItCalculatedThirdItemText,
    },
  ];

  return (
    <AirHelpScoreWrapper
      story={story}
      cmsLanguagesData={cmsLanguagesData}
      serverSideStoryblokPages={serverSideStoryblokPages}
      googleOptimizeExperimentIds={googleOptimizeExperimentIds}
      whiteListDomains={whiteListDomains}
      nofollowUrls={nofollowUrls}
    >
      <Hero heading={heroHeading} subHeading={heroSubHeading} text={heroText} link={heroLink} />
      <TopThree heading={translations("best_airports_pax_exp")}>
        {topThreeAirports.map((item, index) => {
          const {
            dep_airport_name: airportName,
            airport_score: score,
            dep_city_name: city,
            dep_airport_country_name: country,
          } = item;

          return (
            <TopThreeCard key={airportName}>
              <Heading variant="h3" as="h3" mb={2}>
                {airportName}
              </Heading>
              <Text color="greyscale.600" pb={6} justifyContent="none" mb="auto">
                {city}, {country}
              </Text>
              <ScoreTag index={index} score={score} size="lg" />
            </TopThreeCard>
          );
        })}
      </TopThree>
      <AirportRankingTable rows={airportsScores} columns={columns} heading="global_airport_ranking" />
      <HowScoreIsCalculated items={howIsItCalculatedItems} heading={howIsItCalculatedHeading} />
      <GlobalRanking
        heading={globalRankingHeading}
        text={globalRankingText}
        imageSrc={getUrlFromLink(globalRankingImageSrc)}
      />
      <About
        heading={aboutHeading}
        imageSrc={getUrlFromLink(aboutImageSrc)}
        text={aboutText}
        videoSrc={getUrlFromLink(aboutVideoSrc)}
      />
      <JoinTheCommunity />
      <Share />
    </AirHelpScoreWrapper>
  );
};
